// apiErrorHandler.js - Standardized API error handling utility
import logger from './logger';

class APIErrorHandler {
  constructor() {
    this.errorMessages = {
      400: 'Bad request. Please check your input and try again.',
      401: 'Authentication required. Please log in again.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      408: 'Request timeout. Please try again.',
      409: 'Conflict with existing data. Please refresh and try again.',
      422: 'Invalid data provided. Please check your input.',
      429: 'Too many requests. Please wait and try again.',
      500: 'Server error. Please try again later.',
      502: 'Service temporarily unavailable. Please try again later.',
      503: 'Service maintenance. Please try again later.',
      504: 'Gateway timeout. Please try again later.',
    };
  }

  /**
   * Handle API response errors consistently
   * @param {Response} response - Fetch response object
   * @param {Object} context - Additional context for logging
   * @returns {Promise<Object>} - Standardized error object
   */
  async handleResponseError(response, context = {}) {
    const status = response.status;
    // Prefer the request URL from context; response.url can be the dev-server
    // fallback URL when a service worker or redirect is involved.
    const url = context.url || response.url;

    let errorData;

    try {
      errorData = await response.json();
    } catch (e) {
      errorData = null;
    }

    // Extract error message from response
    const errorMessage = this.extractErrorMessage(errorData, status);

    // Log the error with context
    logger.error('API request failed', {
      status,
      url,
      method: context.method || 'GET',
      errorMessage,
      errorData,
      context,
      timestamp: new Date().toISOString(),
    });

    // Return standardized error object
    return {
      status,
      message: errorMessage,
      isNetworkError: false,
      isServerError: status >= 500,
      isClientError: status >= 400 && status < 500,
      shouldRetry: this.shouldRetry(status),
      context,
    };
  }

  /**
   * Handle network/connection errors
   * @param {Error} error - Network error
   * @param {Object} context - Additional context for logging
   * @returns {Object} - Standardized error object
   */
  handleNetworkError(error, context = {}) {
    const errorMessage = this.getNetworkErrorMessage(error);

    logger.error('Network request failed', {
      error: error.message,
      context,
      timestamp: new Date().toISOString(),
    });

    return {
      status: 0,
      message: errorMessage,
      isNetworkError: true,
      isServerError: false,
      isClientError: false,
      shouldRetry: true,
      context,
    };
  }

  /**
   * Extract meaningful error message from response data
   * @param {*} errorData - Parsed error response data
   * @param {number} status - HTTP status code
   * @returns {string} - User-friendly error message
   */
  extractErrorMessage(errorData, status) {
    // Try different possible error message formats
    if (typeof errorData === 'string') {
      return errorData;
    }

    if (errorData?.detail) {
      if (Array.isArray(errorData.detail)) {
        return errorData.detail
          .map(err => typeof err === 'string' ? err : err.msg || err.message || JSON.stringify(err))
          .join(', ');
      }
      return errorData.detail;
    }

    if (errorData?.message) {
      return errorData.message;
    }

    if (errorData?.error) {
      return errorData.error;
    }

    if (errorData?.errors) {
      if (Array.isArray(errorData.errors)) {
        return errorData.errors.join(', ');
      }
      return JSON.stringify(errorData.errors);
    }

    // Fall back to status-based message
    return this.errorMessages[status] || 'An unexpected error occurred. Please try again.';
  }

  /**
   * Get user-friendly network error message
   * @param {Error} error - Network error
   * @returns {string} - User-friendly message
   */
  getNetworkErrorMessage(error) {
    if (error.name === 'AbortError') {
      return 'Request was cancelled. Please try again.';
    }

    if (error.message?.includes('Failed to fetch')) {
      return 'Network connection failed. Please check your internet connection and try again.';
    }

    if (error.message?.includes('NetworkError')) {
      return 'Network error. Please check your connection and try again.';
    }

    if (error.message?.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }

    return 'Network error. Please check your connection and try again.';
  }

  /**
   * Determine if request should be retried
   * @param {number} status - HTTP status code
   * @returns {boolean} - Whether to retry
   */
  shouldRetry(status) {
    // Retry on server errors and some client errors
    return status >= 500 || status === 408 || status === 429;
  }

  /**
   * Create a user-friendly error notification
   * @param {Object} error - Standardized error object
   * @param {Function} showNotification - Notification function
   */
  showErrorNotification(error, showNotification) {
    if (typeof showNotification !== 'function') return;

    let message = error.message;
    let type = 'error';

    // Customize message for specific scenarios
    if (error.isNetworkError) {
      message = 'Connection failed. Please check your internet connection.';
    } else if (error.shouldRetry) {
      message = `${error.message} Please try again in a moment.`;
    } else if (error.status === 401) {
      message = 'Please log in to continue.';
      type = 'warning';
    } else if (error.status === 403) {
      message = 'You do not have permission for this action.';
      type = 'warning';
    }

    showNotification(message, type);
  }

  /**
   * Enhanced fetch with error handling
   * @param {string} url - Request URL
   * @param {Object} options - Fetch options
   * @param {Object} context - Additional context for logging
   * @returns {Promise<Response>} - Fetch response
   */
  async fetchWithErrorHandling(url, options = {}, context = {}) {
    const startTime = Date.now();

    try {
      const response = await fetch(url, options);
      const duration = Date.now() - startTime;

      // Log successful requests
      if (response.ok) {
        logger.api(options.method || 'GET', url, response.status, duration, context);
        return response;
      }

      // Handle error responses
      const error = await this.handleResponseError(response, {
        ...context,
        method: options.method || 'GET',
        duration
      });
      throw error;

    } catch (error) {
      // If it's already a standardized error, re-throw it
      if (error.status !== undefined) {
        throw error;
      }

      // Handle network errors
      const networkError = this.handleNetworkError(error, {
        ...context,
        method: options.method || 'GET'
      });
      throw networkError;
    }
  }
}

// Create singleton instance
const apiErrorHandler = new APIErrorHandler();

export default apiErrorHandler;
